import * as React from "react"

import { cn } from "@/lib/utils"

// Already identical to the hygenius-pria reference card
// (../hygenius-pria/src/components/ui/card.tsx) in every class it renders. The
// one divergence left standing is on CardTitle and CardDescription below.
const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

/**
 * The reference renders this as `<h3>` and CardDescription as `<p>`; both stay
 * `<div>` here. www styles bare `h3` and `p` site-wide for CMS-authored content
 * (styles/globals.css), and the parts of those rules the card sets no utility
 * for would leak into every card outside the PRIA scope — `pb-2` and
 * `text-black` onto each title, `font-light`, `leading-relaxed` and a bottom
 * margin onto each description. That reaches nine non-chat call sites (the
 * contact portal, the thank-you and feedback pages, the contact cards) for no
 * visual gain, since the classes below already win over everything else those
 * base rules set.
 *
 * Switching to the semantic elements is worth doing the day www's bare-element
 * typography is scoped to CMS content — see the note in styles/pria-scope.css.
 */
const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
